// Program scope: the whole AJK public health network, how the pilot and scale-up fill it, and where.

import { AJK_FACILITIES, DISTRICT_CENTRES } from '@shared';
import { AjkMap } from '../ui/AjkMap.tsx';
import { SectionTitle, SiteLayout } from '../ui/site.tsx';

const NETWORK = [
  { type: 'Hospitals (incl. 6 District Headquarter Hospitals)', count: 23, p1: 1, p2: 8, role: 'One DHQ OPD in the pilot; DHQ/THQ OPDs as referral hubs in Phase 2' },
  { type: 'Tehsil / Civil Headquarter Hospitals', count: 11, p1: 0, p2: 6, role: 'Phase 2 referral points' },
  { type: 'Rural Health Centres (RHC)', count: 50, p1: 9, p2: 26, role: 'Core pilot and Phase 2 sites' },
  { type: 'Basic Health Units (BHU)', count: 225, p1: 15, p2: 60, role: 'Core pilot and Phase 2 sites; remote districts first' },
  { type: 'Dispensaries', count: 79, p1: 0, p2: 0, role: 'Considered beyond 100 kiosks (gate X)' },
  { type: 'Mother & Child Health Centres', count: 201, p1: 0, p2: 0, role: 'Beyond 100 kiosks, with a maternal-health configuration' },
];

// Pilot allocation matches the demonstration network; Phase 2 is indicative until the steering committee
// approves the site list from pilot evidence (gate G1), weighted toward remote districts.
const DISTRICTS = [
  { id: 'muzaffarabad', division: 'Muzaffarabad', remote: false, p1: 3, p2: 14 },
  { id: 'neelum', division: 'Muzaffarabad', remote: true, p1: 4, p2: 12 },
  { id: 'hattian-bala', division: 'Muzaffarabad', remote: true, p1: 3, p2: 10 },
  { id: 'bagh', division: 'Poonch', remote: false, p1: 2, p2: 9 },
  { id: 'haveli', division: 'Poonch', remote: true, p1: 3, p2: 10 },
  { id: 'poonch', division: 'Poonch', remote: false, p1: 2, p2: 10 },
  { id: 'sudhnoti', division: 'Poonch', remote: true, p1: 3, p2: 10 },
  { id: 'kotli', division: 'Mirpur', remote: false, p1: 2, p2: 10 },
  { id: 'mirpur', division: 'Mirpur', remote: false, p1: 2, p2: 8 },
  { id: 'bhimber', division: 'Mirpur', remote: false, p1: 1, p2: 7 },
];

const total = NETWORK.reduce((a, r) => a + r.count, 0);
const coreSites = 50 + 225;

export function ScopePage() {
  return (
    <SiteLayout>
      <section className="band-dark hero">
        <div className="wrap">
          <div className="gov-kicker">Program scope</div>
          <h1>The whole AJK network, <span className="gold">filled in measured steps</span></h1>
          <p className="lede">
            Azad Jammu &amp; Kashmir runs {total} public health facilities for more than 4.4 million people. The program begins where the gap is widest —
            Basic Health Units and Rural Health Centres in remote districts — and grows only on the evidence of each phase.
          </p>
          <div className="stat-cards">
            <div className="stat-card"><div className="v">{total}</div><div className="l">public facilities</div></div>
            <div className="stat-card"><div className="v">34</div><div className="l">hospitals (23 + 11 THQ/CHQ)</div></div>
            <div className="stat-card"><div className="v">{coreSites}</div><div className="l">BHUs and RHCs</div></div>
            <div className="stat-card"><div className="v">0.18</div><div className="l">doctors per 1,000 people</div></div>
          </div>
        </div>
      </section>

      <section className="band">
        <div className="wrap">
          <SectionTitle kicker="Facility network">AJK public-sector health facilities and their role</SectionTitle>
          <div className="gtable-wrap">
            <table className="gtable">
              <thead><tr><th>Facility type</th><th className="num">In AJK</th><th className="num">Phase 1 kiosks</th><th className="num">Phase 2 kiosks (indicative)</th><th>Role in the program</th></tr></thead>
              <tbody>{NETWORK.map((r) => <tr key={r.type}><td>{r.type}</td><td className="num">{r.count}</td><td className="num">{r.p1 || '—'}</td><td className="num">{r.p2 || '—'}</td><td>{r.role}</td></tr>)}</tbody>
              <tfoot><tr><td>Total</td><td className="num">{total}</td><td className="num">25</td><td className="num">100</td><td /></tr></tfoot>
            </table>
          </div>
          <p className="gtable-note">Source: AJK IRMNCAH&amp;N Implementation Strategy &amp; Action Plan (National Health Services). Phase 2 split is indicative; the steering committee approves the final list at gate G1.</p>
        </div>
      </section>

      <section className="band band-ivory2">
        <div className="wrap">
          <SectionTitle kicker="Coverage">How each phase fills the network</SectionTitle>
          {[
            ['Phase 1 · pilot', 25, coreSites + 23, 'BHUs, RHCs and one DHQ OPD'],
            ['Phase 2 · program', 100, coreSites + 34, 'BHUs, RHCs, THQ and DHQ OPDs'],
            ['Gate X · extension', total, total, 'Evaluated against the whole network'],
          ].map(([label, n, of, note]) => (
            <div key={String(label)} className="meter-row">
              <div><strong>{label}</strong><div className="muted small">{note}</div></div>
              <div className="meter-track2"><div className="meter-fill2" style={{ width: `${Math.min(100, (Number(n) / Number(of)) * 100)}%` }} /></div>
              <div className="num"><strong>{label === 'Gate X · extension' ? 'up to 589' : `${n} of ${of}`}</strong></div>
            </div>
          ))}
          <div className="card-grid" style={{ marginTop: 24 }}>
            <div className="gcard"><div className="num">PHASE 1</div><h3>25 kiosks · 90-day operating pilot</h3><p>A representative mix across the Muzaffarabad, Poonch and Mirpur divisions, with one DHQ OPD so the full referral chain is tested. Formal review at day 90 of operation.</p></div>
            <div className="gcard"><div className="num">PHASE 2</div><h3>To 100 kiosks, in waves</h3><p>Remote districts first — Neelum, Haveli, Hattian Bala and Sudhnoti — ranked by access gap, patient load, referral role and connectivity. Integrated with the Department’s HIS.</p></div>
            <div className="gcard gold"><div className="num">GATE X</div><h3>Beyond 100</h3><p>Coverage beyond 100 kiosks is evaluated against the 589-facility network, including dispensaries and MCH centres with a maternal-health configuration.</p></div>
          </div>
        </div>
      </section>

      <section className="band">
        <div className="wrap">
          <SectionTitle kicker="By district">Ten districts, three divisions</SectionTitle>
          <div className="gtable-wrap">
            <table className="gtable">
              <thead><tr><th>District</th><th>Division</th><th>Headquarters</th><th className="num">Hospitals mapped</th><th className="num">Pilot kiosks</th><th className="num">Phase 2 (indicative)</th></tr></thead>
              <tbody>
                {DISTRICTS.map((d) => {
                  const c = DISTRICT_CENTRES[d.id];
                  return (
                    <tr key={d.id}>
                      <td><strong>{c.name}</strong> <span lang="ur" style={{ fontFamily: 'var(--font-ur)', color: 'var(--gov-600)' }}>{c.nameUr}</span>{d.remote && <span className="muted small"> · remote priority</span>}</td>
                      <td>{d.division}</td>
                      <td>{c.town}</td>
                      <td className="num">{AJK_FACILITIES.filter((x) => x.district === d.id).length}</td>
                      <td className="num">{d.p1}</td>
                      <td className="num">{d.p2}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr><td>All AJK</td><td /><td /><td className="num">{AJK_FACILITIES.length}</td><td className="num">25</td><td className="num">100</td></tr></tfoot>
            </table>
          </div>
          <div style={{ marginTop: 26 }}>
            <AjkMap title="AJK hospital network" />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
